import { useEffect, useRef, useState } from 'react'
import { Button } from '../../../components/ui/Button'
import { Modal } from '../../../components/ui/Modal'
import type { CreateWorkspaceRequest, WorkspaceVisibility } from '../types'

export interface NewWorkspaceModalProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly onCreate: (request: CreateWorkspaceRequest) => Promise<void>
}

/** New-workspace modal (screens.md §9: "name, description, member invite
 * fields" per components.md's one-off list). Member invites at creation
 * time aren't in the frozen `POST /api/workspaces` contract (name/
 * visibility/description only) — members are added afterward via the
 * workspace detail screen's assign/share modal (`addMember`), so this
 * modal doesn't include an invite field.
 *
 * Copy and focus behaviour are comp-exact (`Fictiv Platform.dc.html:4789-4793`): heading
 * "Create a project", blurb, "Project name" label, "e.g. CNC Router V2"
 * placeholder. Note the comp uses a DIFFERENT string for the entry point that
 * opens this ("New project" — `:2920`, the uploads selector row); don't
 * collapse the two.
 *
 * DIVERGENCE, decided in UID-349: the comp's modal is name-only, while this
 * keeps the visibility radios + description. Removing them would regress
 * UID-48b on `/projects` (this modal is shared — uploads, `/projects`, and the
 * Sidebar rail all render it, matching the comp's single `newProjectModal`) and
 * drop fields the frozen `POST /api/workspaces` contract accepts. */
export function NewWorkspaceModal({ open, onClose, onCreate }: NewWorkspaceModalProps) {
  const [name, setName] = useState('')
  const [visibility, setVisibility] = useState<WorkspaceVisibility>('private')
  const [description, setDescription] = useState('')
  const [busy, setBusy] = useState(false)
  // Comp `:4792` puts `autoFocus` on the name input; `Modal` would otherwise
  // focus the dialog container itself (`Modal.tsx:96`).
  const nameRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) {
      setName('')
      setVisibility('private')
      setDescription('')
    }
  }, [open])

  async function handleSubmit() {
    setBusy(true)
    try {
      await onCreate({
        name: name.trim(),
        visibility,
        ...(description.trim() ? { description: description.trim() } : {}),
      })
      onClose()
    } catch {
      // CONTRACT: `onCreate` owns surfacing the failure (shared toast, inline
      // error — its choice). This modal swallows the rejection so it simply
      // stays open for a retry instead of becoming an unhandled rejection,
      // matching AddMemberModal's established pattern.
      //
      // Named a single caller ("AllWorkspacesPage") until UID-355; there are
      // three, and the contract is what binds them — describing it beats
      // naming one, because a stale name reads as permission for new callers
      // to skip the obligation. As of UID-372, all three call sites
      // (AllWorkspacesPage, UploadsPage, and Sidebar's rail modal) honor it —
      // each wraps its `onCreate` in try/catch, toasts on failure, and
      // rethrows so this swallow-and-stay-open behavior sees the rejection.
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      initialFocusRef={nameRef}
      title="Create a project"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={busy} disabled={name.trim().length === 0}>
            Create project
          </Button>
        </>
      }
    >
      <div className="new-workspace-fields">
        <p className="new-workspace-blurb">
          Projects group parts, BOMs, and orders for a build or team so everyone can find them.
        </p>

        <label className="new-workspace-field" htmlFor="new-workspace-name">
          <span>Project name</span>
          <input
            ref={nameRef}
            id="new-workspace-name"
            type="text"
            placeholder="e.g. CNC Router V2"
            value={name}
            onChange={(event) => setName(event.target.value)}
            // Comp `:4792` wires `onKeyDown` so Enter submits. Guarded on the
            // same condition as the Create button's `disabled`.
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !busy && name.trim().length > 0) {
                event.preventDefault()
                void handleSubmit()
              }
            }}
          />
        </label>

        <fieldset className="new-workspace-visibility">
          <legend>Visibility</legend>
          <label>
            <input
              type="radio"
              name="workspace-visibility"
              checked={visibility === 'private'}
              onChange={() => setVisibility('private')}
            />
            Private — invite only
          </label>
          <label>
            <input
              type="radio"
              name="workspace-visibility"
              checked={visibility === 'account'}
              onChange={() => setVisibility('account')}
            />
            Account — everyone on the account can see it
          </label>
        </fieldset>

        <label className="new-workspace-field" htmlFor="new-workspace-description">
          <span>Description (optional)</span>
          <textarea
            id="new-workspace-description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </label>
      </div>
    </Modal>
  )
}
